import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges
} from '@angular/core';
import { NgxExtendedPdfViewerModule, ZoomType } from 'ngx-extended-pdf-viewer';
import { CbLoadingSpinnerModule } from 'cb-hub-lib';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';

/** pdf.js reports neither loaded nor failed e.g. for a password-protected PDF: then the overlay goes after this */
const VIEWER_LOADING_TIMEOUT_MS = 30000;

const SLOW_LOADING_DELAY_MS = 5000;

@Component({
    selector: 'cb-pdf-viewer',
    standalone: true,
    imports: [NgxExtendedPdfViewerModule, CbLoadingSpinnerModule, MatIcon, MatButton],
    templateUrl: './pdf-viewer.component.html',
    styleUrls: ['./pdf-viewer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PdfViewerComponent implements OnChanges, OnDestroy {
    @Input() public src: Blob | null = null;
    @Input() public documentName: string | null = null;
    @Input() public emptyMessage = '';
    @Input() public emptyDescription = '';
    @Input() public loading = false;
    /** Changes when a new load starts while loading stays true; the slow loading time is counted from then */
    @Input() public loadingKey: unknown = null;
    @Input() public showRetry = false;

    @Output() public retry = new EventEmitter<void>();
    @Output() public loadingFailed = new EventEmitter<void>();

    public resolvedSrc: string | null = null;
    public viewerLoading = false;
    public showSlowLoadingMessage = false;
    /** The library keeps the scrolled-to page for the next document, so it is reset for every source */
    public page = 1;
    public zoom: ZoomType = 'auto';

    private objectUrlToRevoke: string | null = null;
    /** Revoked once the new document loads: revoking a URL pdf.js still reads fails that load */
    private replacedObjectUrls: string[] = [];
    private slowLoadingTimer: ReturnType<typeof setTimeout> | null = null;
    private viewerLoadingTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.src) {
            this.applySource(this.src);
        }
        if (changes.loading?.currentValue || changes.loadingKey) {
            this.stopSlowLoadingTimer();
        }
        this.updateSlowLoadingTimer();
    }

    public onPdfLoaded(): void {
        this.revokeReplacedObjectUrls();
        this.stopViewerLoading();
        this.updateSlowLoadingTimer();
    }

    public onPdfLoadingFailed(): void {
        this.revokeReplacedObjectUrls();
        this.stopViewerLoading();
        this.updateSlowLoadingTimer();
        this.loadingFailed.emit();
    }

    private applySource(input: Blob | null): void {
        if (this.objectUrlToRevoke) {
            this.replacedObjectUrls.push(this.objectUrlToRevoke);
            this.objectUrlToRevoke = null;
        }

        if (!input) {
            this.resetViewerState();
            return;
        }

        this.page = 1;
        this.zoom = 'auto';
        this.startViewerLoading();
        this.objectUrlToRevoke = URL.createObjectURL(input);
        this.resolvedSrc = this.objectUrlToRevoke;
    }

    private resetViewerState(): void {
        this.revokeObjectUrl();
        this.revokeReplacedObjectUrls();
        this.resolvedSrc = null;
        this.stopViewerLoading();
    }

    private startViewerLoading(): void {
        this.stopViewerLoading();
        this.viewerLoading = true;
        this.viewerLoadingTimer = setTimeout(() => {
            this.viewerLoadingTimer = null;
            this.revokeReplacedObjectUrls();
            this.viewerLoading = false;
            this.updateSlowLoadingTimer();
            this.changeDetectorRef.markForCheck();
        }, VIEWER_LOADING_TIMEOUT_MS);
    }

    private stopViewerLoading(): void {
        if (this.viewerLoadingTimer) {
            clearTimeout(this.viewerLoadingTimer);
            this.viewerLoadingTimer = null;
        }
        this.viewerLoading = false;
    }

    private revokeObjectUrl(): void {
        if (!this.objectUrlToRevoke) {
            return;
        }

        URL.revokeObjectURL(this.objectUrlToRevoke);
        this.objectUrlToRevoke = null;
    }

    private revokeReplacedObjectUrls(): void {
        this.replacedObjectUrls.forEach(url => URL.revokeObjectURL(url));
        this.replacedObjectUrls = [];
    }

    private updateSlowLoadingTimer(): void {
        if (!this.loading && !this.viewerLoading) {
            this.stopSlowLoadingTimer();
            return;
        }
        if (this.slowLoadingTimer || this.showSlowLoadingMessage) {
            return;
        }
        this.slowLoadingTimer = setTimeout(() => {
            this.slowLoadingTimer = null;
            this.showSlowLoadingMessage = true;
            this.changeDetectorRef.markForCheck();
        }, SLOW_LOADING_DELAY_MS);
    }

    private stopSlowLoadingTimer(): void {
        if (this.slowLoadingTimer) {
            clearTimeout(this.slowLoadingTimer);
            this.slowLoadingTimer = null;
        }
        this.showSlowLoadingMessage = false;
    }

    public ngOnDestroy(): void {
        this.revokeObjectUrl();
        this.revokeReplacedObjectUrls();
        this.stopSlowLoadingTimer();
        this.stopViewerLoading();
    }
}
